import { supabase } from '../supabaseClient';

const emptyOverview = () => ({
  primaryCase: null,
  pendingTasks: [],
  agreementsDue: [],
  invoicesDue: [],
  unreadMessages: 0,
  documentStatus: { required: 0, submitted: 0, pendingReview: 0 }
});

export const PortalOverviewService = {
  async fetchOverview(client) {
    if (!client?.id) return emptyOverview();
    const overview = emptyOverview();

    try {
      const clientId = client.id;

      const { data: casesData, error: casesError } = await supabase
        .from('cases')
        .select('id,title,status,current_stage,due_date,updated_at,priority')
        .eq('client_id', clientId)
        .order('updated_at', { ascending: false })
        .limit(3);

      if (!casesError && Array.isArray(casesData)) {
        overview.primaryCase = casesData[0] || null;
      }

      const caseIds = Array.isArray(casesData) ? casesData.map((item) => item.id).filter(Boolean) : [];

      const taskQuery = caseIds.length
        ? supabase
            .from('case_tasks')
            .select('id,title,due_date,status,case_id')
            .in('case_id', caseIds)
            .order('due_date', { ascending: true })
            .limit(5)
        : Promise.resolve({ data: [], error: null });

      const queries = [
        taskQuery,
        supabase
          .from('agreements')
          .select('id,title,status,sent_at,due_date,total_amount')
          .eq('client_id', clientId)
          .in('status', ['sent', 'pending'])
          .order('sent_at', { ascending: true })
          .limit(3),
        supabase
          .from('invoices')
          .select('id,invoice_number,status,amount_due,amount_paid,due_date')
          .eq('client_id', clientId)
          .in('status', ['pending', 'overdue'])
          .order('due_date', { ascending: true })
          .limit(3),
        supabase
          .from('communication_history')
          .select('id')
          .eq('client_id', clientId)
          .is('read_at', null),
        supabase
          .from('submission_documents')
          .select('status')
          .eq('client_id', clientId)
      ];

      const [tasksRes, agreementsRes, invoicesRes, messagesRes, documentsRes] = await Promise.allSettled(queries);

      if (tasksRes.status === 'fulfilled' && !tasksRes.value.error) {
        const pending = (tasksRes.value.data || []).filter((task) => task.status !== 'done');
        overview.pendingTasks = pending;
      }

      if (agreementsRes.status === 'fulfilled' && !agreementsRes.value.error) {
        overview.agreementsDue = agreementsRes.value.data || [];
      }

      if (invoicesRes.status === 'fulfilled' && !invoicesRes.value.error) {
        overview.invoicesDue = invoicesRes.value.data || [];
      }

      if (messagesRes.status === 'fulfilled' && !messagesRes.value.error) {
        overview.unreadMessages = messagesRes.value.data?.length || 0;
      }

      if (documentsRes.status === 'fulfilled' && !documentsRes.value.error) {
        const counters = { required: 0, submitted: 0, pending_review: 0 };
        for (const doc of documentsRes.value.data || []) {
          if (!doc?.status) continue;
          if (doc.status === 'required') counters.required += 1;
          else if (doc.status === 'submitted') counters.submitted = (counters.submitted || 0) + 1;
          else if (doc.status === 'pending_review') counters.pending_review += 1;
        }
        overview.documentStatus = {
          required: counters.required,
          submitted: counters.submitted,
          pendingReview: counters.pending_review
        };
      }
    } catch (error) {
      console.error('[PortalOverviewService] fetchOverview failed', error);
    }

    return overview;
  }
};
